/* eslint-disable no-param-reassign */
import createDOMPurify from 'dompurify';
import { isDate, isArray, isObject, isString, isNumber } from 'lodash';
import moment from 'moment';

const DOMPurify = createDOMPurify(window);

// Skip DOMPurify for secret/config fields — special chars like &, <, > must be preserved.
const SENSITIVE_FIELDS = ['password', 'apiKey', 'secretKey', 'headers', 'token', 'accessToken', 'refreshToken'];

/**
 * Decode HTML entities (e.g. &amp; → &, &gt; → >).
 * DOMPurify encodes entities as a side effect of HTML serialization; decode them
 * back so plain-text fields are not corrupted on save.
 */
const decodeHtmlEntities = (html = '') => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

const clearXssError = (text = '') => {
  const textFormat = String(text);
  if (DOMPurify.isSupported) {
    const sanitized = DOMPurify.sanitize(textFormat);
    return decodeHtmlEntities(sanitized);
  }
  return textFormat;
};

const trimData = (text = '') => {
  const regex = new RegExp(/( {2,})/g);
  return text.trim().replace(regex, ' ');
};

const isSensitiveField = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  // Match exact key or suffix after prefix (e.g. s_invoice-password, bimpayment-apiKey)
  const normalized = key.includes('-') ? key.split('-').slice(-1)[0] : key;
  return SENSITIVE_FIELDS.includes(normalized) || SENSITIVE_FIELDS.includes(key);
};

const xssFilter = (value, result) => {
  if (value === undefined || value === null) {
    return value;
  }
  if (moment.isMoment(value)) {
    return value;
  }
  if (isDate(value)) {
    return value;
  }
  if (isNumber(value)) {
    return value;
  }
  if (isArray(value)) {
    value.forEach((item, index) => {
      if (!result[index]) {
        result[index] = {};
      }
      result[index] = xssFilter(item, result[index]);
    });
    result = Object.keys(result).map((key) => result[key]);
  } else if (isObject(value)) {
    Object.keys(value).forEach((key) => {
      if (isSensitiveField(key)) {
        result[key] = value[key];
        return;
      }
      if (!result[key]) {
        result[key] = {};
      }
      result[key] = xssFilter(value[key], result[key]);
    });
  } else if (isString(value)) {
    result = clearXssError(trimData(value));
  } else {
    return value;
  }
  return result;
};

export default xssFilter;
